import PropTypes from "prop-types";
import { usePopper } from "react-popper";
import ListItem from "../atoms/ListItem";
import { Dropdown, Menu } from "../atoms";
import { useTranslation } from "react-i18next";
import InputGroupButton from "./InputGroupButton";
import { WIDTH, SIZE, POSITIONS } from "../constants";
import { useState, useCallback, useEffect } from "react";
import { IconChevronDown } from "@tabler/icons-react";

const propTypes = {
  options: PropTypes.array,
  isDisabled: PropTypes.bool,
  onResult: PropTypes.func,
  defaultLabelButton: PropTypes.string,
  size: PropTypes.oneOf([SIZE.sm, SIZE.md, SIZE.lg, SIZE.xl, SIZE["2xl"]]),
  width: PropTypes.oneOf([WIDTH.xs, WIDTH.sm, WIDTH.md, WIDTH.lg, WIDTH.xl]),
};

function InputGroupDropdown({
  size,
  options,
  onResult,
  inputValue,
  isDisabled,
  width = WIDTH.lg,
  defaultLabelButton,
  ...props
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [popperElement, setPopperElement] = useState(null);
  const [inputChange, setInputChange] = useState(inputValue);
  const [itemLabelButton, setItemLabelButton] = useState("");
  const [referenceElement, setReferenceElement] = useState(null);

  const setButtonLabel = useCallback(() => {
    setIsLoading(true);
    if (options?.length && defaultLabelButton) {
      const res = options.filter(({ value }) => {
        if (value?.toLowerCase() === defaultLabelButton.toLowerCase()) {
          return value;
        }
        return null;
      })[0]?.value;

      setItemLabelButton(res);
    } else {
      setItemLabelButton(options[0]?.value);
    }
    setIsLoading(false);
  }, [options, defaultLabelButton]);

  useEffect(() => setButtonLabel(), [setButtonLabel]);

  const measuredRef = useCallback((node) => {
    if (node !== null) {
      setReferenceElement(node);
    }
  }, []);

  const offset = useCallback(
    ({ reference, popper }) => {
      if (props.buttonPosition === POSITIONS.left) {
        return [-((reference.width - popper.width) / 2), 0];
      } else {
        return [(reference.width - popper.width) / 2, 0];
      }
    },
    [props.buttonPosition]
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom",
    modifiers: [
      {
        name: "offset",
        options: {
          offset,
        },
      },
    ],
    strategy: "absolute",
  });

  const renderOptions = () => {
    if (options?.length === 0) {
      return <ListItem disabled>{t("dropdown.no_matches_found")}</ListItem>;
    }

    return options?.map(({ value, icon }, index) => (
      <ListItem
        icon={icon}
        value={value}
        key={`list-item-input-group-${index}`}
      >
        {value}
      </ListItem>
    ));
  };

  const handleItemSelected = (item) => {
    setItemLabelButton(item);
    onResult({ item, input: inputChange });
  };

  const handleInputChange = (input) => {
    setInputChange(input);
    onResult({ item: itemLabelButton, input });
  };

  return (
    <Dropdown
      size={size}
      width={width}
      className="!w-fit"
      inputValue={inputChange}
      isDisabled={isDisabled}
      handleChangeInput={false}
      onSelect={handleItemSelected}
      onInputValueChange={handleInputChange}
    >
      <InputGroupButton
        isLoading={isLoading}
        buttonRef={measuredRef}
        icon={<IconChevronDown />}
        labelButton={itemLabelButton}
        {...props}
      />
      <Menu
        scrollable
        className="!w-fit"
        style={styles.popper}
        menuRef={setPopperElement}
        {...attributes.popper}
      >
        {renderOptions()}
      </Menu>
    </Dropdown>
  );
}

InputGroupDropdown.propTypes = propTypes;
export default InputGroupDropdown;
